import { styled } from "goober";
import { type ReactNode, useRef } from "react";
import type { ConnectorStyle } from "../../../lib/routeConnectors";
import {
  MapChromeCorner,
  MapChromePill,
  MapChromePressButton,
} from "../../MapChrome";
import { Icon } from "../../Icon";
import { MapConnectorStyleControl } from "../../MapConnectorStyleControl";
import { metaJoin } from "../../MetaSeparator";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import type { EdgeType, GraphNode } from "./buildAccessModel";
import { edgeVisual } from "./edgeColor";

const ChromeRail = styled("div")`
  position: absolute;
  right: 12px;
  bottom: 12px;
  left: 12px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 12px;
  pointer-events: none;
`;

const ContextCorner = styled(MapChromeCorner)`
  position: static;
  min-width: 0;
  max-width: 680px;
`;

const ControlsCorner = styled(MapChromeCorner)`
  position: static;
  flex-shrink: 0;
  margin-left: auto;
`;

const ContextPill = styled(MapChromePill)`
  min-width: 0;
  max-width: 680px;
  padding: 0 10px;
  gap: 8px;

  &[data-has-actions="true"] {
    padding-right: 4px;
  }
`;

const ContextText = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`;

const Visual = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Control = styled(MapChromePressButton)`
  width: auto;
  height: 24px;
  padding: 0 8px 0 6px;
  gap: 5px;
  border-radius: 38px;
  background: transparent;
  white-space: nowrap;

  &:hover {
    background: var(--alpha4);
  }
  &[data-on="true"] {
    background: var(--alpha4);
  }
  &[data-on="true"]:hover {
    background: var(--alpha8);
  }
  & > * {
    opacity: 0.4;
    transition: opacity 120ms ease;
  }
  &[data-on="true"] > * {
    opacity: 1;
  }
`;

const LineSample = styled("svg")`
  overflow: visible;
  flex-shrink: 0;
`;

function LineGlyph({ type }: { type: EdgeType }) {
  const { stroke, dash } = edgeVisual(type, "active");
  return (
    <LineSample width="16" height="2" aria-hidden="true">
      <line
        x1="0"
        y1="1"
        x2="16"
        y2="1"
        stroke={stroke}
        strokeWidth="1"
        strokeDasharray={dash}
        strokeLinecap="round"
      />
    </LineSample>
  );
}

function LayerControl({
  on,
  glyph,
  label,
  tooltip,
  onToggle,
  layer,
  onHoverLayer,
}: {
  on: boolean;
  glyph: ReactNode;
  label: string;
  tooltip: string;
  onToggle: () => void;
  layer?: EdgeType;
  onHoverLayer: (layer: EdgeType | null) => void;
}) {
  return (
    <Tooltip content={tooltip} contentStyle={{ maxWidth: 200 }}>
      <Control
        data-no-pill
        data-on={on ? "true" : undefined}
        aria-label={label}
        aria-pressed={on}
        onPress={onToggle}
        onMouseEnter={() => {
          if (layer) onHoverLayer(layer);
        }}
        onMouseLeave={() => {
          if (layer) onHoverLayer(null);
        }}
        onMouseDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
        }}
      >
        {glyph}
        <Text variant="text-small-md" color="inherit">
          {label}
        </Text>
      </Control>
    </Tooltip>
  );
}

const MoreAnchor = styled("span")`
  display: flex;
  flex-shrink: 0;
`;

type Props = {
  visible: boolean;
  node: GraphNode | null;
  visual: ReactNode;
  summary: string[];
  hideUnrelated: boolean;
  onToggleHide: () => void;
  showDirect: boolean;
  onToggleDirect: () => void;
  showInherited: boolean;
  onToggleInherited: () => void;
  showAvailable: boolean;
  onToggleAvailable: () => void;
  connectorStyle: ConnectorStyle;
  connectorStyleUrl: string;
  onHoverLayer: (layer: EdgeType | null) => void;
  onMore: (node: GraphNode, anchor: HTMLElement) => void;
};

export function AccessMapChrome({
  visible,
  node,
  visual,
  summary,
  hideUnrelated,
  onToggleHide,
  showDirect,
  onToggleDirect,
  showInherited,
  onToggleInherited,
  showAvailable,
  onToggleAvailable,
  connectorStyle,
  connectorStyleUrl,
  onHoverLayer,
  onMore,
}: Props) {
  const moreAnchorRef = useRef<HTMLSpanElement>(null);
  return (
    <ChromeRail data-map-chrome-rail>
      <ContextCorner data-side="left">
        <ContextPill
          data-visible={visible ? "true" : undefined}
          data-touch-persistent="true"
          data-has-actions={node ? "true" : undefined}
        >
          {node ? (
            <>
              <Visual>{visual}</Visual>
              <ContextText>
                <Text
                  variant="text-small-md"
                  color="var(--text-primary)"
                  ellipsis
                >
                  {node.name}
                </Text>
                {summary.length > 0 && (
                  <Text variant="text-small" color="var(--text-secondary)">
                    {metaJoin(summary)}
                  </Text>
                )}
              </ContextText>
              <MoreAnchor ref={moreAnchorRef}>
                <MapChromePressButton
                  data-no-pill
                  aria-label={`More actions for ${node.name}`}
                  onPress={() => {
                    if (moreAnchorRef.current)
                      onMore(node, moreAnchorRef.current);
                  }}
                  onMouseDown={(event) => {
                    if (event.button === 0) event.preventDefault();
                  }}
                >
                  <Icon name="more" size={16} color="currentColor" />
                </MapChromePressButton>
              </MoreAnchor>
            </>
          ) : (
            <Text variant="text-small" color="var(--text-secondary)" ellipsis>
              Click to focus&nbsp;&nbsp;•&nbsp;&nbsp;Drag to grant
              access&nbsp;&nbsp;•&nbsp;&nbsp;Click ⋮ for actions
            </Text>
          )}
        </ContextPill>
      </ContextCorner>

      <ControlsCorner data-side="right">
        <MapChromePill
          data-visible={visible ? "true" : undefined}
          data-touch-persistent="true"
        >
          <LayerControl
            on={hideUnrelated}
            glyph={<Icon name="layers" size={10} />}
            label="Collapse"
            tooltip="Hide everything but the selected card and its links, closing the gaps."
            onToggle={onToggleHide}
            onHoverLayer={onHoverLayer}
          />
          <LayerControl
            on={showDirect}
            glyph={<LineGlyph type="direct" />}
            label="Direct"
            tooltip="Group memberships and access granted straight to a person or group."
            onToggle={onToggleDirect}
            layer="direct"
            onHoverLayer={onHoverLayer}
          />
          <LayerControl
            on={showInherited}
            glyph={<LineGlyph type="inherited" />}
            label="Inherited"
            tooltip="Access a person gets through a group they belong to, shown for the selected card."
            onToggle={onToggleInherited}
            layer="inherited"
            onHoverLayer={onHoverLayer}
          />
          <LayerControl
            on={showAvailable}
            glyph={<LineGlyph type="derived" />}
            label="Available"
            tooltip="Connectors and skills a space can use through its members."
            onToggle={onToggleAvailable}
            layer="derived"
            onHoverLayer={onHoverLayer}
          />
          <MapConnectorStyleControl
            connectorStyle={connectorStyle}
            to={connectorStyleUrl}
          />
        </MapChromePill>
      </ControlsCorner>
    </ChromeRail>
  );
}
